import { type ReactElement } from "react";
import {
  widgetTargetKey,
  type GenericTableParams,
} from "@playbookai/widget-schemas";
import { Input } from "../../atoms/Input/Input.js";
import { Textarea } from "../../atoms/Textarea/Textarea.js";
import { EditableDataGrid } from "../../molecules/EditableDataGrid/EditableDataGrid.js";
import type { EditableDataGridProps } from "../../molecules/EditableDataGrid/EditableDataGrid.js";
import type { WidgetBodyProps } from "../types.js";
import {
  useFieldEditSession,
  useFieldEditSessionMap,
} from "../field-edit-session.js";
import { usePeopleSource } from "../user-directory.js";

export function GenericTableExpandedEditor({
  data,
  onChange,
  readOnly,
  columnPolicy,
  rowDeletePolicy,
  onDeleteRow,
  rowGroup,
  rowBadge,
}: WidgetBodyProps<GenericTableParams> &
  Pick<
    EditableDataGridProps,
    "columnPolicy" | "rowDeletePolicy" | "onDeleteRow" | "rowGroup" | "rowBadge"
  >): ReactElement {
  const disabled = readOnly === true;
  // The canvas's people (assign-person d7): user cells become person fields.
  const people = usePeopleSource();
  // PB-4923 AC5 — the table's two describing fields. The GRID's own leases are
  // wired through `EditableDataGrid`'s `cellTargetKey`/`columnTargetKey`, so a
  // cell is claimed as `rows[r]/cells{c}` exactly as the executor addresses it.
  const titleLease = useFieldEditSession("title");
  const descriptionLease = useFieldEditSession("description");
  const cellLease = useFieldEditSessionMap();
  // `rows[r]/cells{c}` — an ENTRY, not a prop. That is the whole point of the
  // record segment: two people editing two cells of one row are independent
  // (AC1), so the lease has to name the pair rather than the row.
  const cellSession = (rowId: string, columnId: string) =>
    cellLease(
      widgetTargetKey([
        { kind: "item", collection: "rows", itemId: rowId },
        { kind: "entry", record: "cells", key: columnId },
      ]),
    );
  return (
    <div className="flex min-h-0 flex-col gap-5 p-5">
      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="Table title"
          value={data.title}
          disabled={disabled}
          {...titleLease}
          onChange={(event) => onChange({ ...data, title: event.target.value })}
        />
        <Textarea
          label="Description"
          value={data.description}
          disabled={disabled}
          {...descriptionLease}
          onChange={(event) =>
            onChange({ ...data, description: event.target.value })
          }
        />
      </div>
      {data.binding ? (
        <div className="rounded-md border border-pb-border bg-pb-card-elevated/50 px-3 py-2 text-xs text-pb-text-muted">
          Bound to {data.binding.skillId} · {data.binding.templateId} v
          {data.binding.templateVersion}
        </div>
      ) : null}
      <EditableDataGrid
        columns={data.columns}
        rows={data.rows}
        readOnly={disabled}
        {...(people !== null ? { peopleSource: people } : {})}
        {...(columnPolicy !== undefined ? { columnPolicy } : {})}
        {...(rowDeletePolicy !== undefined ? { rowDeletePolicy } : {})}
        {...(onDeleteRow !== undefined ? { onDeleteRow } : {})}
        {...(rowGroup !== undefined ? { rowGroup } : {})}
        {...(rowBadge !== undefined ? { rowBadge } : {})}
        cellSession={cellSession}
        onChange={({ columns, rows }, options) =>
          options
            ? onChange({ ...data, columns, rows }, options)
            : onChange({ ...data, columns, rows })
        }
      />
    </div>
  );
}
